import { ComponentType, ReactNode } from 'react';
import {
  ComponentProductDetails,
  GetPdpModalConfigAction,
  PageType,
  RenderPdpModalHero,
  VerticalType,
} from '../../types/cmsTypes';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { LoanSearchParams } from '../../utils/searchParams/loanSearchParams';
import { ProductDetailsModalMode } from './ProductDetailModalMode';
import { ProductDetailsPageMode } from './ProductDetailPageMode';

export type ProductDetailsProps = ComponentProductDetails & {
  vertical?: VerticalType;
  pageType?: PageType;
  pushDataLayer?: PushDataLayerFn;
  /**
   * MODAL mode only: app-provided fetcher for the canonical PDP page's benefit
   * filter, plus the preview flag its cache bypass keys off — both travel as
   * props, like `ProductBlockAsideModal`'s `getProductAction` (CMS-1924).
   * PageMode ignores them.
   */
  getPdpModalConfigAction?: GetPdpModalConfigAction;
  isPreview?: boolean;
  renderPdpModalHero?: RenderPdpModalHero;
  /**
   * Apply link that replaces the product's own in the module's CTAs — the panel hero and the sticky
   * footer (CMS-2043): the opener's link in the modal, the page hero's authored CTA on the full page
   * (set by the fe ProductDetailsBlock).
   */
  ctaLinkOverride?: string;
  // `tiEligibilityRows` (TI M6 eligibility rows) is inherited from
  // ComponentProductDetails — mapped from the component's `ti_eligibility` field.
  // TI provider page (M5) nav extras, supplied by the fe wrapper and read by the
  // TI builder's StickyNavigationBarTi (the modes spread the props into the nav).
  stickyOffset?: number;
  tierSlot?: ReactNode;
  siblingNavigationItems?: { id: string; label: string }[];
  // TI only: the fe wrapper supplies a `GlobalStickyPanel`-backed container so
  // the nav pins via portal-to-body + `position: fixed` (CSS `sticky` breaks in
  // the nested TI page layout — an ancestor `overflow`/`transform`/`contain`).
  // CC/PL leaves this undefined and keeps the plain CSS-`sticky` bar.
  navStickyContainer?: ComponentType<{ children: ReactNode }>;
  // TI only: whether the `navStickyContainer` is currently pinned. Drives the
  // nav's pinned-only styling (e.g. the desktop fade strip behind the Apply
  // CTA), which must stay transparent while the nav sits in flow. CC/PL keeps
  // its bar backgrounded at all times, so it leaves this undefined.
  navPinned?: boolean;
  // PL only: the page's loan amount/tenure (URL `cms_amount`/`cms_tenure` over
  // the defaults), read and written through the fe PL wrapper so the Offers
  // section and the loan calculator share one selection.
  loanSearchParams?: LoanSearchParams;
  onLoanSearchParamsChange?: (params: LoanSearchParams) => void;
};

export const ProductDetails: React.FC<ProductDetailsProps> = (props) => {
  const { renderingMode } = props;

  return renderingMode === 'MODAL' ? (
    <ProductDetailsModalMode {...props} />
  ) : (
    <ProductDetailsPageMode {...props} />
  );
};
